import React, { FC, useEffect, useRef, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { Button, Row, Col } from 'react-bootstrap'
import { Formik, Form as FormikForm } from 'formik'
import * as Yup from 'yup'
import { publicApi, useAuth } from '../../../auth'
import { handleAxiosErrors } from '../forms/errors/Helper'
import { useToast } from '../toast/hooks/useToast'
import Common from '../Common'
import TextField from '../forms/fields/TextField'
import { useNavigate } from 'react-router-dom'
import fullLogoDark from '../../../client/logo/logo-full-dark.svg'
import { Messages } from 'primereact/messages'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'

type FormObject = {
    email: string
    code?: string
    new_password?: string
    new_password_confirmation?: string
}

const getValidationSchema = (emailValidated: boolean) => {
    return emailValidated
        ? Yup.object().shape({
              email: Yup.string()
                  .trim()
                  .email('Invalid email format')
                  .required('Required'),
              code: Yup.number().required('Required'),
              new_password: Yup.string().trim().required('Required'),
              new_password_confirmation: Yup.string()
                  .trim()
                  .required('Required')
                  .oneOf(
                      [Yup.ref('new_password'), ''],
                      'Passwords must match'
                  ),
          })
        : Yup.object().shape({
              email: Yup.string()
                  .trim()
                  .email('Invalid email format')
                  .required('Required'),
          })
}

const AppInitiatedDialog: FC = () => {
    const { setAppInitiated } = useAuth()
    const emptyFormObject: FormObject = {
        email: '',
    }

    const [emailValidated, setEmailValidated] = useState<boolean>(false)
    const [formObject] = useState<FormObject>(emptyFormObject)
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
    const [errMsg, setErrMsg] = useState('')

    const errRef = useRef<HTMLDivElement>(null)
    const msgs = useRef<any>(null)
    const formikRef = useRef<{ validateForm: () => Promise<Record<string, string>>; setTouched: (touched: Record<string, boolean>) => void; submitForm: () => void } | null>(null)

    const { triggerToast } = useToast()

    const navigate = useNavigate()

    const initiateAccount = async (record: any, formik: any) => {
        setErrMsg('')
        setIsSubmitting(true)

        try {
            const routePath = '/auth/finalise-account'

            const response = await publicApi
                .post(routePath, record, {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true,
                })
                .finally(() => {
                    setIsSubmitting(false)
                })

            if (response.data?.success === true) {
                triggerToast({
                    severity: 'success',
                    summary: 'Successful',
                    detail: emailValidated
                        ? 'Your account has been successfully setup. You may login now.'
                        : "We've emailed you your activation code. Don't forget to check your spam!",
                })
                setEmailValidated(true)
                // formik.resetForm()

                if (response.data?.data?.initiated === true) {
                    setAppInitiated(true)
                    navigate('/login')
                }
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err) {
            // console.log('err: ', err)
            handleAxiosErrors(err, setErrMsg)
        }
    }

    useEffect(() => {
        setErrMsg('')
    }, [formObject])

    useEffect(() => {
        if (errMsg) {
            msgs.current?.show({
                id: '1',
                sticky: true,
                severity: 'error',
                summary: '',
                detail: errMsg,
                closable: false,
            })
        } else {
            msgs.current?.clear()
        }
    }, [errMsg])

    const handleFooterSubmit = () => {
        const formik = formikRef.current
        if (!formik) return
        formik.validateForm().then((errors) => {
            if (Object.keys(errors).length > 0) {
                formik.setTouched(
                    Object.fromEntries(Object.keys(errors).map((k) => [k, true]))
                )
                // Defer submit so React commits touched state before next validation
                setTimeout(() => formik.submitForm(), 0)
            } else {
                formik.submitForm()
            }
        })
    }

    const formFooter = (
        <div className="d-flex flex-wrap justify-content-center gap-2 w-100">
            <Button
                type="button"
                variant="primary"
                disabled={isSubmitting}
                onClick={handleFooterSubmit}
            >
                {isSubmitting ? (
                    <>
                        <FontAwesomeIcon icon={faCircleNotch} spin />{' '}
                        {emailValidated ? 'Saving...' : 'Sending code...'}
                    </>
                ) : emailValidated ? (
                    'Save'
                ) : (
                    'Send Code'
                )}
            </Button>
        </div>
    )

    const FormContent = (
        <div className="app-initiated-dialog__body">
            {isSubmitting && <Common.Loader text="Loading..." />}
            <div
                ref={errRef}
                className={
                    'text-danger small ' + (errMsg ? 'errmsg' : 'offscreen')
                }
                aria-live="assertive"
            >
                <Messages ref={msgs} />
            </div>
            <Formik
                initialValues={{
                    ...formObject,
                }}
                validationSchema={getValidationSchema(emailValidated)}
                onSubmit={(values, formik) => {
                    initiateAccount(values, formik)
                }}
                enableReinitialize={true}
            >
                {(formikProps: any) => {
                    formikRef.current = formikProps
                    const handleFormSubmit = (e: React.FormEvent) => {
                        e.preventDefault()
                        handleFooterSubmit()
                    }
                    return (
                        <FormikForm onSubmit={handleFormSubmit}>
                            <Row>
                                <Col xl={12}>
                                    <TextField
                                        id="formOwnerEmail"
                                        name="email"
                                        label="Email"
                                        placeholder="Enter your email"
                                        formikProps={formikProps}
                                        readonly={emailValidated}
                                    />
                                </Col>
                                {emailValidated ? (
                                    <>
                                        <Col xl={12}>
                                            <TextField
                                                id="formInitiationCode"
                                                type="number"
                                                name="code"
                                                label="Verification Code"
                                                placeholder="Enter code"
                                                formikProps={formikProps}
                                            />
                                        </Col>
                                        <Col xl={6}>
                                            <TextField
                                                id="formNewPassword"
                                                type="password"
                                                name="new_password"
                                                label="New Password"
                                                placeholder="***********"
                                                formikProps={formikProps}
                                            />
                                        </Col>
                                        <Col xl={6}>
                                            <TextField
                                                id="formNewPasswordConfirmation"
                                                type="password"
                                                name="new_password_confirmation"
                                                label="Confirm New Password"
                                                placeholder="***********"
                                                formikProps={formikProps}
                                            />
                                        </Col>
                                    </>
                                ) : (
                                    <></>
                                )}
                            </Row>
                            <input
                                type="submit"
                                id="app-initiated-form-submit-btn"
                                className="d-none"
                            />
                        </FormikForm>
                    )
                }}
            </Formik>
        </div>
    )

    return (
        <Dialog
            visible={true}
            breakpoints={{ '1240px': '50vw', '960px': '90vw', '640px': '90vw' }}
            style={{ width: '35vw' }}
            className="app-initiated-dialog p-fluid"
            draggable={false}
            header={
                <>
                    <div className="app-initiated-dialog__hero">
                        <img
                            src={fullLogoDark}
                            alt="Full Logo"
                            className="app-initiated-dialog__logo"
                        />
                        <h2 className="app-initiated-dialog__title">Finalising your account</h2>
                    </div>
                </>
            }
            modal
            dismissableMask={false}
            footer={formFooter}
            onHide={() => setAppInitiated(true)}
            closeOnEscape={false}
            closable={false}
            maskClassName="bg-gradient-primary"
        >
            {FormContent}
        </Dialog>
    )
}

export default AppInitiatedDialog
